/* eslint-disable react/no-array-index-key */
import * as React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { InsertLink } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import './index.scss';
import { cloneDeep } from 'lodash';
import { useEffect } from 'react';
import { ICreativeDocuments } from '../../../../../config/interfaces';
import { useDeleteCreativeDocumentMutation, useUpdateDocumentTypeMutation } from '../../../../../../graphql/generated/graphql';
import { useLoginState } from '../../../../../hooks/loginInfo';
import { UserTypes } from '../../../../../config/enums';
import { setAlertDescription } from '../../../../../hooks/actions';

const StyledTableCell: any = styled(TableCell)(({ theme }) => ({
  padding: '0.5rem',
  paddingLeft: '1rem',
  paddingRight: '1rem',
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    fontSize: 12,
    color: 'black',
    fontWeight: 'bold',
    padding: '1rem',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  borderRadius: '5px',
  // '&:last-child td, &:last-child th': {
  //   border: 0,
  // },
}));

const TableComponent = (props: any) => {
  const { documents }: { documents: Array<ICreativeDocuments> } = props;
  const { isLoading, canEditing } = props;
  const [docs, setDocs] = React.useState<Array<ICreativeDocuments>>(documents);
  const [updateDocumentType] = useUpdateDocumentTypeMutation();
  const [deleteCreativeDocument] = useDeleteCreativeDocumentMutation();

  const handleChange = async (event: SelectChangeEvent, doc: ICreativeDocuments) => {
    try {
      const updateDocs = cloneDeep(docs);
      const index = documents.findIndex((d) => d.id === doc.id);
      updateDocs[index].document_type = event.target.value;
      setDocs(updateDocs);
      await updateDocumentType({
        variables: {
          documents: [{
            document_id: doc.id,
            document_type: event.target.value,
          }],
        },
      }).then((res) => {
        setAlertDescription({
          open: true,
          message: 'The Category has been updated succesfully',
          severity: 'success',
        })
      });
    } catch (error) {
      console.error(error);
    }
  };
  const loginState = useLoginState();
  const userType = loginState.user_type.get();

  useEffect(() => {
    setDocs(documents);
  }, [documents])

  const deleteDocument = async (doc: ICreativeDocuments) => {
    try {
      await deleteCreativeDocument({
        variables: {
          documents: [doc.id],
        },
      }).then((res) => {
        setAlertDescription({
          open: true,
          message: 'The File/Link has been deleted succesfully',
          severity: 'success',
        })
        setDocs(docs.filter((d) => d.id !== doc.id));
      });
    } catch (error) {
      console.error(error);
    }
  }

  const downloadFile = (doc: ICreativeDocuments) => {
    window.open(doc.file
      ? doc.file
      : doc.link_url?.match(/^https?:/)
        ? doc.link_url
        : `//${doc.link_url}`);
  }

  return (
    <Grid item xs={12}>
      <Table
        sx={{
          borderRadius: '0',
          width: '100%',
          overflowX: 'auto'
        }}
        aria-label="customized table"
        className="table-creative-docs"
      >
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>Files Name</StyledTableCell>
            <StyledTableCell>File Type</StyledTableCell>
            <StyledTableCell>Date</StyledTableCell>
            <StyledTableCell>Author</StyledTableCell>
            <StyledTableCell style={{ textAlign: 'center' }}>Action</StyledTableCell>
          </StyledTableRow>
        </TableHead>
        {
          !isLoading && docs.length > 0 &&
          (
            <TableBody>
              {
                docs.map((doc, index: number) => (
                  <StyledTableRow key={`${index}${doc.file_name}`}>
                    <StyledTableCell>
                      <Link
                        variant="body1"
                        href={doc.link_url?.match(/^https?:/)
                          ? doc.link_url
                          : `//${doc.link_url}` || doc.file}
                        target="_blank"
                        sx={{
                          textDecoration: 'none',
                          color: 'black',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        {doc.file_name}
                      </Link>
                    </StyledTableCell>
                    <StyledTableCell>
                      <FormControl variant="standard" fullWidth>
                        <Select
                          className='font-weight-normal'
                          labelId="file-type"
                          id="file-type-select"
                          value={doc.document_type}
                          label="FileType"
                          onChange={(event) => handleChange(event, doc)}
                          disabled={!canEditing}
                        >
                          <MenuItem
                            className='font-weight-normal'
                            value="WITHOUT_CATEGORY"
                          >
                            Category
                          </MenuItem>
                          <MenuItem
                            className='font-weight-normal'
                            value="SCRIPTS"
                          >
                            Scripts
                          </MenuItem>
                          <MenuItem
                            className='font-weight-normal'
                            value="STORY_BOARDS"
                          >
                            Storyboards
                          </MenuItem>
                          <MenuItem
                            className='font-weight-normal'
                            value="MOOD_BOARDS"
                          >
                            Mood Boards
                          </MenuItem>
                          <MenuItem
                            className='font-weight-normal'
                            value="SHOT_LIST"
                          >
                            Shot List
                          </MenuItem>
                          <MenuItem
                            className='font-weight-normal'
                            value="ANIMATICS"
                          >
                            Animatics
                          </MenuItem>
                          <MenuItem
                            className='font-weight-normal'
                            value="VIDEO"
                          >
                            Video
                          </MenuItem>
                          <MenuItem
                            className='font-weight-normal'
                            value="DOCUMENTS"
                          >
                            Documents
                          </MenuItem>
                          <MenuItem
                            className='font-weight-normal'
                            value="OTHER"
                          >
                            Other
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </StyledTableCell>
                    <StyledTableCell
                      className='font-weight-normal'
                    >
                      {doc.date}
                    </StyledTableCell>
                    <StyledTableCell
                      className='font-weight-normal'
                    >
                      {doc.created_by}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <IconButton onClick={() => downloadFile(doc)}>
                          {doc.link_url ? <InsertLink /> : <DownloadIcon />}
                        </IconButton>
                        {
                          userType === UserTypes.PRODUCER && (
                            <IconButton onClick={() => deleteDocument(doc)}>
                              <DeleteIcon />
                            </IconButton>
                          )
                        }
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              }
            </TableBody>
          )
        }
      </Table>
      {
        isLoading &&
        (
          <Box
            sx={{
              textAlign: 'center',
              padding: '20px'
            }}
          >
            <CircularProgress
              color="secondary"
              size={35}
            />
          </Box>
        )
      }
      {
        (!isLoading && docs.length === 0) &&
        (
          <Box
            sx={{
              textAlign: 'center',
              padding: '20px'
            }}
          >
            <Typography
              component="p"
              sx={{
                fontWeight: 500,
              }}
            >
              You don't have any file
            </Typography>
          </Box>
        )
      }
    </Grid>
  )
}

export default TableComponent;
